import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import logo from './logo.png';

// Import logos
import googleLogo from './assets/google-logo.png';
import facebookLogo from './assets/facebook-logo.png';
import instagramLogo from './assets/instagram-logo.png';
import whatsappLogo from './assets/whatsapp-logo.png';
import xhsLogo from './assets/xhs-logo.png';
import logo202 from './assets/202-logo.png';
import tiktokLogo from './assets/tiktok-logo.png';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #FFE4C4 0%, #FFF8DC 50%, #FFDAB9 100%);
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem 3rem;
  color: #2C3E50;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 1rem 1rem 2rem;
  }

  @keyframes gradientMove {
    0% { background-position: 0% 50% }
    50% { background-position: 100% 50% }
    100% { background-position: 0% 50% }
  }
`;

const HeaderSection = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  margin-top: 2rem;
  padding: 3rem 2rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 24px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(230, 126, 34, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(to right, #E67E22, #D35400);
  }

  @media (max-width: 768px) {
    margin-top: 1rem;
    padding: 2rem 1.5rem;
  }
`;

const Logo = styled.img`
  height: clamp(100px, 15vw, 150px);
  width: auto;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    height: clamp(80px, 12vw, 120px);
    margin-bottom: 1rem;
  }
`;

const Title = styled.h1`
  font-family: 'Quicksand', sans-serif;
  font-size: clamp(2.2rem, 5.5vw, 3.8rem);
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 800;
  background: linear-gradient(to right, #E67E22, #D35400);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.02em;
  line-height: 1.3;
  position: relative;
  padding-bottom: 1rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 3px;
    background: linear-gradient(to right, #E67E22, #D35400);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: clamp(2rem, 4.5vw, 2.8rem);
    margin-bottom: 1rem;
  }
`;

const Subtitle = styled.h2`
  font-family: 'Quicksand', sans-serif;
  font-size: clamp(1.3rem, 3.2vw, 2.2rem);
  text-align: center;
  margin-bottom: 3rem;
  font-weight: 500;
  line-height: 1.8;
  max-width: 800px;
  color: #34495E;

  span {
    color: #E67E22;
    font-weight: 700;
    font-style: italic;
  }

  @media (max-width: 768px) {
    font-size: clamp(1.1rem, 2.8vw, 1.6rem);
    margin-bottom: 2rem;
    padding: 0 1rem;
    line-height: 1.6;
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Quicksand', sans-serif;
  font-size: clamp(1.3rem, 2.8vw, 2rem);
  color: #2C3E50;
  text-align: center;
  margin: 3rem 0 2rem;
  font-weight: 600;
  position: relative;
  padding-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background: #E67E22;
    border-radius: 1px;
  }

  @media (max-width: 768px) {
    font-size: clamp(1.2rem, 2.5vw, 1.7rem);
    margin: 2rem 0 1.5rem;
  }
`;

const GridSection = styled.section`
  width: 100%;
  max-width: 1200px;
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 24px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  margin: 2rem 0;
  border: 1px solid rgba(230, 126, 34, 0.1);

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
`;

const SocialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  padding: 0 1rem;
  margin-bottom: 2rem;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 0.5rem;
  }
`;

const GoogleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  padding: 0 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 0.5rem;
  }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  border: 1px solid rgba(230, 126, 34, 0.1);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, #E67E22, #D35400);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
    border-color: rgba(230, 126, 34, 0.2);

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 8px;
  background: transparent;
  mix-blend-mode: multiply;

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const GoogleIconImage = styled(IconImage)`
  width: 40px;
  height: 40px;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #2C3E50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Button = styled.a`
  background: #E67E22;
  color: white;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  -webkit-tap-highlight-color: transparent;
  text-decoration: none;
  display: inline-block;
  text-align: center;
  width: 200px;
  margin: 0.5rem 0;
  box-shadow: 0 2px 4px rgba(230, 126, 34, 0.2);
  font-family: 'Quicksand', sans-serif;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, rgba(255,255,255,0.1), rgba(255,255,255,0));
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(230, 126, 34, 0.3);
    background: #D35400;
    color: white;

    &::after {
      transform: translateX(100%);
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(230, 126, 34, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.7rem 1.8rem;
    font-size: 1rem;
    width: 180px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.5rem;
  align-items: center;
`;

function App() {
  const cardVariants = {
    hover: {
      scale: 1.02,
      transition: {
        duration: 0.2
      }
    }
  };

  const branchInfo = [
    {
      name: "Butterworth",
      reviewLink: "https://g.co/kgs/reh5dwW",
      mapLink: "https://maps.app.goo.gl/Gji4E3Lh7F3xnv5i9"
    },
    {
      name: "Farlim",
      reviewLink: "https://g.co/kgs/Qh1bWd5",
      mapLink: "https://maps.app.goo.gl/jACu85EGswrSX4o8A"
    },
    {
      name: "Bayan Lepas",
      reviewLink: "https://g.co/kgs/bXiYxdT",
      mapLink: "https://maps.app.goo.gl/8kPC7ZwaWG6uWZus6"
    },
    {
      name: "Bukit Mertajam",
      reviewLink: "https://g.co/kgs/tCazNTF",
      mapLink: "https://maps.app.goo.gl/GPVQXsJWmriLAu4Z7"
    },

  ];

  return (
    <Container>
      <HeaderSection>
        <Logo src={logo} alt="Company Logo" />
        <Title>火锅烧烤要齐全，肯定是来 202 ！</Title>
        <Subtitle>
          One Stop Solution for <br/><span>steamboat</span> & <span>bbq</span> lovers!
        </Subtitle>
      </HeaderSection>

      <GridSection>
        <SectionTitle>
          还没 follow 我们？小心错过限时 promo 哦～👇社媒连结都给你安排好了！<br/>
          Follow us for all updates and promo!👇All our social media links are right here!
        </SectionTitle>
        <SocialGrid>
          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={logo202} alt="202 Website" />
            </IconWrapper>
            <CardTitle>Visit Our Website</CardTitle>
            <Button href="https://202foodpg.com/" target="_blank" rel="noopener noreferrer">Visit Now</Button>
          </Card>

          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={xhsLogo} alt="RedNote" />
            </IconWrapper>
            <CardTitle>Find Us on Red Note</CardTitle>
            <Button href="https://www.xiaohongshu.com/user/profile/6057e904000000000100a53d?xsec_token=YBPZn1D7lEEuvE1-0oPM3j-LL2BEepbTNMCWW1Uvcohu0=&xsec_source=app_share&xhsshare=CopyLink&appuid=6057e904000000000100a53d&apptime=1749628748&share_id=b1c5a55f71924d62857264190ff86b81" target="_blank" rel="noopener noreferrer">Open Page</Button>
          </Card>

          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={tiktokLogo} alt="TikTok" />
            </IconWrapper>
            <CardTitle>Watch Us on TikTok</CardTitle>
            <Button href="https://www.tiktok.com/@202foodpg?_r=1&_t=ZS-971iNcUQGRw" target="_blank" rel="noopener noreferrer">Open TikTok</Button>
          </Card>

          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={instagramLogo} alt="Instagram" />
            </IconWrapper>
            <CardTitle>Follow us on Instagram</CardTitle>
            <Button href="https://www.instagram.com/202foodpg/" target="_blank" rel="noopener noreferrer">Open Instagram</Button>
          </Card>

          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={facebookLogo} alt="Facebook" />
            </IconWrapper>
            <CardTitle>View Us on Facebook</CardTitle>
            <Button href="https://www.facebook.com/202FoodPG/" target="_blank" rel="noopener noreferrer">Open Facebook</Button>
          </Card>

          <Card whileHover="hover" variants={cardVariants}>
            <IconWrapper>
              <IconImage src={whatsappLogo} alt="WhatsApp" />
            </IconWrapper>
            <CardTitle>Contact us on WhatsApp</CardTitle>
            <Button href="https://wa.me/60125919202 " target="_blank" rel="noopener noreferrer">Chat Now</Button>
          </Card>
        </SocialGrid>

        <SectionTitle>
          要知道门市在哪？📍马上点击！<br/>
          Wanna know where our stores are?📍Click now to find out!
        </SectionTitle>
        <GoogleGrid>
          {branchInfo.map((branch, index) => (
            <Card key={index} whileHover="hover" variants={cardVariants}>
              <IconWrapper>
                <GoogleIconImage src={googleLogo} alt="Google" />
              </IconWrapper>
              <CardTitle>{branch.name}</CardTitle>
              <ButtonGroup>
                <Button 
                  href={branch.reviewLink}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Review us
                </Button>
                <Button 
                  href={branch.mapLink}
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Get Directions
                </Button>
              </ButtonGroup>
            </Card>
          ))}
        </GoogleGrid>
      </GridSection>
    </Container>
  );
}

export default App;
